import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PartyPopper, Users, Flame, Shuffle, Bell, Mail, Lock, Sun, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const RozluckovaSection = () => {
  const [email, setEmail] = useState('');

  const targetTypes = [
    { id: 'zenich', label: 'Ženich', icon: '🤵' },
    { id: 'nevesta', label: 'Nevěsta', icon: '👰' },
    { id: 'smisena', label: 'Smíšená', icon: '🎉' },
  ];

  const difficulties = [
    { id: 'lehka', label: 'Lehká', desc: 'Zábavná a bezpečná', icon: Sun },
    { id: 'legendarna', label: 'Legendární', desc: 'Trapné situace zaručeny', icon: Flame },
    { id: 'mix', label: 'Mix', desc: 'Loterie překvapení', icon: Zap },
  ];

  const packages = [
    { id: 'maly', label: 'Malý', tasks: '10 úkolů', price: '179 Kč' },
    { id: 'stredni', label: 'Střední', tasks: '20 úkolů', price: '197 Kč', recommended: true },
    { id: 'velky', label: 'Velký', tasks: '30 úkolů + certifikát', price: '247 Kč' },
  ];

  return (
    <section id="rozluckova" className="py-20 bg-white">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--vp-mint)]/20 rounded-full text-sm font-medium text-[var(--vp-ink)] mb-6">
              <PartyPopper className="w-4 h-4" />
              Připravujeme
            </div>

            <h2 className="text-3xl sm:text-4xl font-semibold text-[var(--vp-ink)] mb-6">
              Rozlučková výzva
            </h2>

            <p className="text-lg text-[var(--vp-ink)]/70 mb-4">
              Digitální hra vytvořená na míru pro ženicha, nevěstu
              nebo smíšenou rozlučku.
            </p>

            <p className="text-[var(--vp-ink)]/70 mb-8">
              Vyberete typ rozlučky, obtížnost a velikost balíčku.
              Systém vám následně vygeneruje unikátní sadu úkolů,
              které můžete číst z mobilu nebo vytisknout.
            </p>

            {/* Image */}
            <div className="rounded-3xl overflow-hidden shadow-[var(--shadow-soft)] border border-[var(--vp-line)] mb-8">
              <img
                src="https://images.pexels.com/photos/5303406/pexels-photo-5303406.jpeg"
                alt="Rozlučková party"
                className="w-full h-64 object-cover"
              />
            </div>

            
          </motion.div>

          {/* Right */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-[var(--vp-paper)] rounded-3xl p-8 border border-[var(--vp-line)] relative">

              {/* Overlay */}
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] rounded-3xl z-10 flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 mb-3 px-6 py-2 text-base bg-[var(--vp-mint)] rounded-full">
                    <Bell className="w-5 h-5" />
                    Připravujeme
                  </div>
                  <p className="text-[var(--vp-ink)]/60 text-sm">
                    Brzy si budete moci objednat
                  </p>
                </div>
              </div>

              {/* Preview form */}
              <div className="space-y-6 opacity-60">
                
                {/* Target */}
                <div>
                  <label className="block text-sm font-medium mb-3">
                    <Users className="w-4 h-4 inline mr-2" />
                    Pro koho je rozlučka?
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {targetTypes.map((type) => (
                      <button key={type.id} disabled className="p-3 rounded-xl border text-center">
                        <span className="text-2xl block mb-1">{type.icon}</span>
                        <span className="text-sm">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty */}
                <div>
                  <label className="block text-sm font-medium mb-3">
                    <Flame className="w-4 h-4 inline mr-2" />
                    Obtížnost
                  </label>
                  <div className="space-y-2">
                    {difficulties.map((diff) => (
                      <button key={diff.id} disabled className="w-full p-3 rounded-xl border flex items-center gap-3">
                        <diff.icon className="w-5 h-5" />
                        <div>
                          <p>{diff.label}</p>
                          <p className="text-xs opacity-60">{diff.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Packages */}
                <div>
                  <label className="block text-sm font-medium mb-3">
                    <Shuffle className="w-4 h-4 inline mr-2" />
                    Velikost balíčku
                  </label>
                  <div className="space-y-2">
                    {packages.map((pkg) => (
                      <button key={pkg.id} disabled className="w-full p-4 rounded-xl border flex justify-between">
                        <div>
                          <p>{pkg.label}</p>
                          <p className="text-sm opacity-60">{pkg.tasks}</p>
                        </div>
                        <p>{pkg.price}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <Button disabled className="w-full py-4 bg-gray-300">
                  <Lock className="w-4 h-4 mr-2" />
                  Brzy k dispozici
                </Button>

              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default RozluckovaSection;